import {useEffect, useMemo, useReducer, useRef} from "react";
import {formatDate} from "../../../utils/formatDate";
import {formatNumber} from "../../../utils/formatNumber";
import DataTransformWorker from "./dataTransform.worker.js?worker";
import {DEFAULT_LINE_COLORS} from "./graphColorPalette";
import {resolveTimeFormat} from "./timeFormat";

// Action types
const ACTIONS = {
  DATA_LOADING: "DATA_LOADING",
  DATA_LOADED: "DATA_LOADED",
  DATA_ERROR: "DATA_ERROR",
  SET_X_DOMAIN_LEFT: "SET_X_DOMAIN_LEFT",
  SET_X_DOMAIN_RIGHT: "SET_X_DOMAIN_RIGHT",
  SET_SHOW_MESSAGE: "SET_SHOW_MESSAGE",
  ZOOM_IN: "ZOOM_IN",
  ZOOM_OUT: "ZOOM_OUT",
};

// Initial state
const initialState = {
  chartData: [],
  isLoading: true,
  isZoomed: false,
  showMessage: false,
  timeOnXAxis: false,
  timeOnYAxis: false,
  visibleData: [],
  xDomainLeft: "",
  xDomainRight: "",
};

// Reducer function
const chartDataReducer = (state, action) => {
  const {payload, type} = action;
  switch (type) {
    case ACTIONS.DATA_LOADING:
      return {
        ...state,
        isLoading: true,
      };
    case ACTIONS.DATA_LOADED:
      return {
        ...state,
        chartData: payload.chartData,
        isLoading: false,
        timeOnXAxis: payload.timeOnXAxis,
        timeOnYAxis: payload.timeOnYAxis,
        visibleData: payload.chartData,
      };
    case ACTIONS.DATA_ERROR:
      return {
        ...state,
        chartData: [],
        isLoading: false,
        visibleData: [],
      };
    case ACTIONS.SET_X_DOMAIN_LEFT:
      return {
        ...state,
        xDomainLeft: payload,
      };
    case ACTIONS.SET_X_DOMAIN_RIGHT:
      return {
        ...state,
        xDomainRight: payload,
      };
    case ACTIONS.SET_SHOW_MESSAGE:
      return {
        ...state,
        showMessage: payload,
      };
    case ACTIONS.ZOOM_IN:
      return {
        ...state,
        isZoomed: true,
        showMessage: payload.showMessage,
        visibleData: payload.filteredData,
        xDomainLeft: "",
        xDomainRight: "",
      };
    case ACTIONS.ZOOM_OUT:
      return {
        ...state,
        isZoomed: false,
        showMessage: false,
        visibleData: state.chartData,
        xDomainLeft: "",
        xDomainRight: "",
      };
    default:
      return state;
  }
};

export const useHandleChartData = (props) => {
  const {jsonData, properties, timeZone = "UTC"} = props;
  const {colors, line_keys: lines = [], timeFormat, xColumn} = properties;
  const [state, dispatch] = useReducer(chartDataReducer, initialState);
  const messageShownRef = useRef(false);
  const timeoutRef = useRef(null);

  const colorHexes = useMemo(() => {
    if (colors && colors.length) {
      return colors.map((color) => color.hex);
    }
    return DEFAULT_LINE_COLORS.map((color) => color.hex);
  }, [colors]);

  useEffect(() => {
    dispatch({type: ACTIONS.DATA_LOADING});
    const worker = new DataTransformWorker();
    worker.postMessage({jsonData, lines, xColumn});
    worker.onmessage = (e) => {
      const {chartData, timeOnXAxis, timeOnYAxis} = e.data;
      dispatch({
        type: ACTIONS.DATA_LOADED,
        payload: {chartData, timeOnXAxis, timeOnYAxis},
      });
      worker.terminate();
    };
    worker.onerror = () => {
      dispatch({type: ACTIONS.DATA_ERROR});
      worker.terminate();
    };

    return () => {
      worker.terminate();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [jsonData, lines, xColumn]);

  const formatValue = (value, axis, axisProperties) => {
    const timeOnAxis = {
      x: state.timeOnXAxis,
      y: state.timeOnYAxis,
    };
    const formattedValue = timeOnAxis[axis]
      ? formatDate(value, resolveTimeFormat(timeFormat), timeZone)
      : typeof value === "number"
        ? formatNumber(value)
        : value;
    return `${axisProperties.prefix}${formattedValue}${axisProperties.suffix}`;
  };

  const zoomIn = () => {
    if (state.xDomainLeft === state.xDomainRight || state.xDomainRight === "") {
      dispatch({type: ACTIONS.SET_X_DOMAIN_LEFT, payload: ""});
      dispatch({type: ACTIONS.SET_X_DOMAIN_RIGHT, payload: ""});
      return;
    }

    let _xDomainLeft = state.xDomainLeft;
    let _xDomainRight = state.xDomainRight;

    const dataValues = state.chartData.map((e) => e[xColumn]);
    let fromIndex = dataValues.indexOf(_xDomainLeft);
    let toIndex = dataValues.indexOf(_xDomainRight);

    // Handle reverse selection (dragging from right to left)
    if (fromIndex > toIndex) {
      [fromIndex, toIndex] = [toIndex, fromIndex];
    }

    const filteredData = state.chartData.slice(fromIndex, toIndex + 1);
    const showMessage = !messageShownRef.current;
    if (showMessage) {
      messageShownRef.current = true;

      // Set showMessage to false after 5 seconds
      timeoutRef.current = setTimeout(() => {
        dispatch({type: ACTIONS.SET_SHOW_MESSAGE, payload: false});
      }, 5000);
    }
    dispatch({type: ACTIONS.ZOOM_IN, payload: {filteredData, showMessage}});
  };

  const zoomOut = () => {
    if (!state.isZoomed) return;
    dispatch({type: ACTIONS.ZOOM_OUT});
  };

  return {
    chartData: state.chartData,
    colorHexes,
    formatValue,
    isLoading: state.isLoading,
    setXDomainLeft: (value) =>
      dispatch({type: ACTIONS.SET_X_DOMAIN_LEFT, payload: value}),
    setXDomainRight: (value) =>
      dispatch({type: ACTIONS.SET_X_DOMAIN_RIGHT, payload: value}),
    showMessage: state.showMessage,
    timeOnXAxis: state.timeOnXAxis,
    timeOnYAxis: state.timeOnYAxis,
    visibleData: state.visibleData,
    xDomainLeft: state.xDomainLeft,
    xDomainRight: state.xDomainRight,
    zoomIn,
    zoomOut,
  };
};
